<template>
  <div id="app">
    <pro-layout v-if="status.success">
      <template #mainCore>
        <router-view />
        <MicroAppContainer />
      </template>
    </pro-layout>
    <ApplyRoles />
    <div v-if="!status.success && status.errorMessage" style="padding-top: 200px">
      <pb-empty :description="status.errorMessage"></pb-empty>
    </div>
    <div v-if="!status.success && !status.errorMessage" v-loading="!status.success" class="main-loading"
      :element-loading-text="$t('soda-b-web-i18n---加载中' /*加载中*/)"></div>
  </div>
</template>
<script setup>
import { onMounted, provide, reactive, getCurrentInstance } from 'vue'
import { Message, Notification } from '@didi-pebble/pebble-design-vue'
import { registerMicroApps, start } from 'qiankun'
import { addRoute, env, ENV_kEYS } from '@/utils'
import {
  eventBus,
  getMicroAppsQianKunProps,
  gtrVuePlugin,
  lifeCycles,
  getMicroAppsQianKunConfig,
  routerHooks,
  routerPath,
  fullPath,
  checkMicroAppName,
  gtrRouter,
  gtrListenEvents,
} from '@/micro-app'
import { request } from '@/plugins/plugin-request'
import { Omega, omegaSetConfig } from '@/plugins/plugin-init-omega'
import { useStore } from '@/store'
import { getUserInfo } from '@/services'
import { DEFAULT_NAVIGATOR_LOCAL } from '@/config/constants'
import { getPrefetchNum } from '@/config/apps'
import vueI18n from '@/i18n/locale'
import router from '@/router'
import ProLayout from '@/pages/layout'
import MicroAppContainer from '@/components/micro-app-container'
import { gtrAuth, gtrAuths } from '@/utils/gtr-auth'
import initFormilyI18n from '@/plugins/formily'
import pbLocale from '@didi-pebble/pebble-design-vue/src/locale'
import { LANG_MAP } from '@/plugins/pebble-ui'
import { startInterval } from '@/utils/display-time'
import ApplyRoles from '@/components/applyRoles/index.vue'
import FeedbackCenterList from '@/components/feedback-center-list'
import { getSystem } from '@/utils/system-implant'
const userInfo = env.get(ENV_kEYS.userInfo)

const store = useStore()

const status = reactive({
  success: false,
  errorMessage: '',
})

const instance = getCurrentInstance()

/**
 * 初始化微前端
 * @returns {boolean}
 */
function initMicroApps() {
  // 根据用户拜访项目的频次，设置预加载项目
  let prefetchApps = (userInfo?.visitProjects || []).filter(name => {
    return checkMicroAppName(name)
  })
  const prefetchNum = getPrefetchNum()
  if (prefetchApps.length > prefetchNum) {
    prefetchApps = prefetchApps.slice(0, prefetchNum)
  }
  // 注册微应用
  registerMicroApps(
    getMicroAppsQianKunProps({
      GTR_USREINFO: userInfo,
      GTR_SYSTEM_IMPLANT: getSystem(),
      GTR_AUTH_COUNTRY: store.app.userAuthCountry,
      GTR_UTILS: {
        eventBus, // omega实install例
        router: gtrRouter, // 基座vue实例
        routerHooks, // 路由勾子
        routerPath, // 根据子应用path获取基座路由path
        fullPath, // 根据子应用path获取基座fullpath
        request, // 请求方法
        vuePlugin: gtrVuePlugin,
        appConfig: {}, // name 、appLocation 、activeRule 、 entry
        omega: Omega, // omega实例
        gtrAuth,
        gtrAuths,
      },
    }),
    lifeCycles,
  )
  // 启动微应用
  start({
    prefetch: prefetchApps,
    excludeAssetFilter(assetUrl) {
      return getMicroAppsQianKunConfig().ignoreScript.some(item => {
        return assetUrl.includes(item)
      })
    },
  })
}

/**
 * 创建动态路由
 */
function initAsyncRouter() {
  // 获取参数
  const menuList = store.menu.routeList
  // 添加动态路由
  addRoute(undefined, menuList)
}

/**
 * 初始化业务线信息
 */
function initProductLines() {
  store.app.initSidesList()
}

/**
 * 设置vueI18n语言
 */
function setI18nLocal() {
  const locale = userInfo?.locale || DEFAULT_NAVIGATOR_LOCAL
  vueI18n.locale = locale
  initFormilyI18n(locale) // 初始化formily相关翻译设置
  pbLocale.use(LANG_MAP[locale]) // 初始化pebble国际化 http://pebble.design.intra.didiglobal.com/vue/#/zh-CN/component/i18n
  provide('locale', locale) // 带优化 pro-component 国际化 http://pebble.design.intra.didiglobal.com/vue/pro-components/ProProvider.html
}

/**
 * 增加水印
 */
function initWaterMark() {
  if (process.env.NODE_ENV === 'production') {
    window.waterMark &&
      window.waterMark({
        systemId: 'sailingGattaran', // UPM系统注册系统ID
        userId: userInfo.username,
        textStyle: 'rgba(0, 0, 0, 0.03)',
      })
  }
}

/**
 * 获取全量的FeatureAuth
 */
function initAllFeatureAuth() {
  getUserInfo().then(res => {
    const feature_auth = res?.data?.data?.feature_auth || {}
    env.update(ENV_kEYS.featureAuth, feature_auth)
    if (res?.data?.data) {
      eventBus.$emit('gtr_new_userInfo', true)
    }
  })
}

/**
 * 做一些公共参数的预校验
 * @returns {boolean}
 */
function checkCommonParams() {
  store.app.initUserAuthCountry()
  // 检查国家 / 时区 /语言 相关参数，没有的话弹窗强制设置。弹窗组件处理
  if (!userInfo.country || !userInfo.locale || !userInfo.timezone) {
    // 初始化国家信息
    status.errorMessage = ''
    status.success = true
    return false
  }
  return true
}

const listenFeedbackState = state => {
  switch (state) {
    case 'send':
      FeedbackCenterList.sendOpen(request)
      break
    case 'view':
      FeedbackCenterList.viewOpen(request)
      break
  }
}

// 基座注册
function initGtrListenEvents() {
  onMounted(() => {
    gtrListenEvents.GTR_LISTEN_EVENT_PERMISSION_NOTIFY(data => {
      if (!(data?.reqParams?.key || data?.reqParams?.api))
        return console.warn('key or api is undefined')
      const h = instance.proxy.$createElement
      Notification({
        title: vueI18n.t('Gattaran_Homepage__ssGO' /** 缺少权限 */, { node_info: '' }),
        message: h('div', null, data?.reqParams?.key || data?.reqParams?.api),
        customBtn: [
          {
            value: vueI18n.t('Gattaran_Feedback__EhcV' /** 反馈 */),
            actions: () => {
              listenFeedbackState('send')
            },
          },
          {
            value: vueI18n.t('Gattaran_Homepage__XQjL' /** 申请权限 */),
            styleObject: {
              marginLeft: '10px',
            },
            actions: () => {
              const { name, nodeId } = store.app.moduleInfo
              eventBus.$emit('GTR_LISTEN_EVENT_APPLY_ROLE_DEAWER', {
                moduleName: name || vueI18n.t('FoodOpB_Authority2_Role_List_OQWs' /** 角色列表 */),
                nodeId: data?.reqParams?.nodeId || nodeId,
              })
            },
          },
        ],
        type: 'warning',
        duration: 5 * 1000,
        customClass: 'gtr-permission-notify',
        dangerouslyUseHTMLString: true,
      })
    })
    // 跳转无权限页面
    gtrListenEvents.GTR_LISTEN_EVENT_PERMISSION_PAGES(({ nodeId, refer = '' }) => {
      if (!nodeId) return console.warn('nodeId is undefined')
      router.push({
        path: '/no-permission',
        query: {
          nodeId,
          refer,
        },
      })
    })
  })
}

/**
 * 初始化应用
 */
async function initApp() {
  if (!checkCommonParams()) return
  try {
    // 启动时间轮训
    startInterval()
    initGtrListenEvents()
    omegaSetConfig()
    initWaterMark()
    setI18nLocal()
    initAllFeatureAuth()
    // 初始化全量菜单信息，方便概述页取用
    // await store.menu.initMenuList();
    // 初始化异步动态路由
    initAsyncRouter()
    // 应用初始化完成
    status.errorMessage = ''
    status.success = true
    initMicroApps()
    initProductLines()
  } catch (err) {
    console.warn(err)
    if (err?.errno === 10000) {
      // sign in again
    } else {
      status.success = false
      status.errorMessage = err?.errmsg || err
      Message.error(err?.errmsg || err)
    }
  }
}

initApp()
</script>
<style>
/* 暂时修复toast层级低于b营销弹窗页面的场景; */
.pb-message {
  z-index: 9999 !important;
}

#app {
  /*font-family: DiDiSans-Pro-Medium, SF Pro Text, PingFang SC, PingFang TC, Meiryo, Microsoft Sans Serif, Microsoft YaHei, Microsoft JhengHei, Helvetica Neue, Helvetica, Arial, Sans-serif;*/
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  /*顶部导航最窄574*/
  min-width: 560px;
  overflow-x: scroll;
}

#app .main-loading {
  width: 100vw;
  height: 100vh;
}

.gtr-permission-notify.pb-notification {
  width: 396px !important;
}

/*.gtr-permission-notify .pb-notification__title {*/
/*  font-size: 14px;*/
/*}*/
</style>

<style lang="scss" scoped>
.pb-popup-parent--hidden {
  .fixed-header {
    padding-right: 15px;
  }
}
</style>
